import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { openRepositories, type Repositories } from '@core/db';
import { openFileStore, type FileStore } from '@core/files';

export interface AppData {
  db: Repositories;
  files: FileStore;
}

const DataContext = createContext<AppData | null>(null);

/**
 * Слой данных приложения, открытый один раз на всё время работы.
 *
 * База и файловое хранилище открываются вместе: экран, которому нужны документы,
 * бесполезен без обоих. Пока они открываются, не показываем ничего — мелькнувший
 * пустой список хуже одного пустого кадра, а открытие занимает доли секунды.
 */
export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Открытие затянулось: пустой экран без слов юрист читает как поломку. */
  const [isSlow, setSlow] = useState(false);

  useEffect(() => {
    let dropped = false;
    let db: Repositories | null = null;
    let files: FileStore | null = null;

    /*
     * Открытие и закрытие идут строго по очереди.
     *
     * iOS шлёт `pagehide` и `pageshow` подряд и без пауз — переключение приложений
     * туда-обратно занимает полсекунды. Без очереди возврат начинал открывать базу
     * раньше, чем уход её закрыл, и в живых оставались два соединения к одному файлу.
     */
    let queue: Promise<unknown> = Promise.resolve();

    /*
     * Часы «открытие затянулось» заводятся на **каждое** открытие.
     *
     * Один таймер на всю жизнь экрана годился, пока открытие было одно. Теперь их
     * столько, сколько раз юрист уходил в фон и возвращался, и однажды показанная
     * надпись висела бы потом при каждом переключении приложений — там, где открытие
     * занимает долю секунды и говорить не о чем.
     */
    let slow: ReturnType<typeof setTimeout> | undefined;

    const startClock = () => {
      clearTimeout(slow);
      setSlow(false);
      // Четыре секунды — это уже не «доли секунды», обещанные ниже. Значит, надо сказать.
      slow = setTimeout(() => !dropped && setSlow(true), 4000);
    };

    const open = () => {
      startClock();
      queue = queue
        .then(async () => {
          if (dropped || db) return;

          /*
           * Оба открываются **разом**, а не по очереди.
           *
           * Очередь стоила секунд на запуске: открытие базы — самое долгое, что
           * делает приложение (wasm плюс файловая система), и ждать перед ним ещё
           * и хранилище незачем. Замерено 10.09.2026 прогоном e2e: на последовательном
           * открытии четыре набора из тех же тестов упирались в тридцатисекундное
           * ожидание оболочки, на параллельном — нет.
           *
           * Само файловое хранилище переоткрывать не надо: соединений оно не держит.
           */
          const [opened, store] = await Promise.all([openRepositories(), files ?? openFileStore()]);
          if (dropped) return void opened.close();

          files = store;
          // Просим не вытеснять данные: внутри материалы дел, а не кэш картинок.
          await store.requestPersistence();

          db = opened;
          setData({ db: opened, files: store });
          /*
           * Удачное открытие **снимает** прежнюю ошибку.
           *
           * Без этого одна осечка оставляла экран отказа навсегда: возврат из фона
           * открывал базу заново и заполнял `data`, но `error` так и стоял, а он
           * проверяется первым. Приложение было живо, а показывало отказ — до тех
           * пор, пока юрист не закроет его полностью.
           */
          setError(null);
          clearTimeout(slow);
        })
        .catch((reason: unknown) => {
          if (!dropped) setError(String(reason));
        });
    };

    /**
     * Уход со страницы закрывает базу — и **возврат обязан открыть её заново**.
     *
     * Закрытие нужно: SQLite поверх IndexedDB держит записанные страницы в кэше своей
     * файловой системы и сбрасывает их не сразу. Юрист добавлял документ, уходил
     * на другой экран — и запись пропадала, хотя файл на диске оставался.
     *
     * Но `pagehide` на iOS приходит **и когда приложение уходит в фон**, а не только
     * при закрытии вкладки. Обработчик стоял с `once: true`, открыть базу обратно было
     * некому — и юрист, вернувшийся из другого приложения, получал экран без данных:
     * каждый запрос падал с «bad parameter or other API misuse». Воспроизведено
     * в WebKit 10.09.2026, разбор — PITFALLS §17.
     *
     * `pagehide`, а не `beforeunload`: на iOS второе не срабатывает вовсе.
     */
    const hide = () => {
      queue = queue
        .then(async () => {
          const opened = db;
          if (!opened) return;

          // Ссылка снимается до закрытия: возврат не должен закрыть базу второй раз.
          db = null;

          /*
           * Данные с экрана **не снимаются**, хотя соединение уже закрыто.
           *
           * Напрашивается обратное: `setData(null)` на время закрытия, и тогда никто
           * не обратится к закрытой базе. Попробовано 15.09.2026 — и откачено тем же
           * днём. Сброс размонтирует `AppShell`, а вместе с ним уходит вся навигация:
           * юрист переключился на почту, вернулся — и он не в карточке дела, где был,
           * а в списке дел. Поймано тестом «карточка дела открывается после возврата»
           * из этого же файла; заодно так же терялся бы набранный, но не сохранённый
           * текст в открытом диалоге.
           *
           * Окно, в котором запрос может уйти в закрытую базу, остаётся — оно длится
           * время переоткрытия. Закрывать его надо не размонтированием дерева,
           * а очередью в самих репозиториях: BACKLOG, «запрос ждёт открытия базы».
           * До тех пор падение видно — его ловит `ErrorBoundary`, и юрист получает
           * причину на экране, а не белизну.
           */
          await opened.close();
        })
        .catch(() => undefined);
    };

    open();
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', open);

    return () => {
      dropped = true;
      clearTimeout(slow);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', open);
      void queue.then(() => db?.close());
    };
  }, []);

  if (error) {
    return (
      <div className="data-error" role="alert">
        <p>Не удалось открыть хранилище.</p>
        <p className="data-error__reason">{error}</p>
      </div>
    );
  }

  /*
   * Пока хранилище открывается, не показываем ничего: мелькнувший пустой список хуже
   * одного пустого кадра, а открытие занимает доли секунды. Но если оно затянулось,
   * пустой кадр превращается в белый экран без объяснений — и юрист прав, читая его
   * как поломку. Поэтому через четыре секунды экран начинает говорить.
   */
  if (!data) {
    return (
      <div className="data-splash" role="status">
        {isSlow ? (
          <p className="data-splash__note">
            Открываем хранилище. Если это не проходит, закройте приложение полностью
            и откройте заново.
          </p>
        ) : null}
      </div>
    );
  }

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
}

/** Данные приложения. Вызывается только внутри [DataProvider]. */
export function useData(): AppData {
  const data = useContext(DataContext);
  if (!data) throw new Error('useData вызван вне DataProvider');

  return data;
}
